import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Award, Download, Share2, CheckCircle, Calendar,
  BookOpen, Shield, ExternalLink, Copy, ArrowLeft, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const API = process.env.REACT_APP_BACKEND_URL;

// Certificate display component
function CertificateCard({ certificate, showActions = true }) {
  const certificateRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const handleCopyLink = () => {
    const verifyUrl = `${window.location.origin}/financial-literacy/verify/${certificate.certificate_number}`;
    navigator.clipboard.writeText(verifyUrl);
    toast.success('Verification link copied!');
  };

  const handleDownloadPDF = async () => {
    if (!certificateRef.current) return;
    
    setDownloading(true);
    try {
      // Temporarily hide action buttons for screenshot
      const actionsDiv = certificateRef.current.querySelector('[data-actions]');
      if (actionsDiv) actionsDiv.style.display = 'none';
      
      // Generate canvas from certificate element
      const canvas = await html2canvas(certificateRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false
      });
      
      // Restore action buttons
      if (actionsDiv) actionsDiv.style.display = '';
      
      // Create PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'px',
        format: [canvas.width, canvas.height]
      });
      
      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
      
      // Download
      const fileName = `DowUrk_Certificate_${certificate.certificate_number}.pdf`;
      pdf.save(fileName);
      
      toast.success('Certificate downloaded!');
    } catch (err) {
      console.error('PDF generation error:', err);
      toast.error('Failed to download certificate');
    } finally {
      setDownloading(false);
    }
  };

  const handleShare = async () => {
    const verifyUrl = `${window.location.origin}/financial-literacy/verify/${certificate.certificate_number}`;
    const shareData = {
      title: `DowUrk Financial Literacy Certificate - ${certificate.module_name || certificate.track_name}`,
      text: `I earned a certificate for completing ${certificate.module_name || certificate.track_name} in the DowUrk Financial Literacy Program!`,
      url: verifyUrl
    };
    
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        if (err.name !== 'AbortError') {
          handleCopyLink();
        }
      }
    } else {
      handleCopyLink();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative"
    >
      <Card ref={certificateRef} className="border-2 border-[#006847]/30 bg-gradient-to-br from-white to-green-50/50 overflow-hidden">
        {/* Certificate Border Pattern */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-4 border-2 border-[#006847]/20 rounded-lg" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-white rounded-full flex items-center justify-center border-4 border-[#006847]">
            <Award className="h-8 w-8 text-[#006847]" />
          </div>
        </div>

        <CardContent className="pt-12 pb-8 px-8 text-center relative">
          {/* Header */}
          <div className="mb-6">
            <p className="text-xs tracking-[0.3em] text-gray-500 uppercase mb-2">
              Certificate of Completion
            </p>
            <h2 className="text-sm text-[#006847] font-medium">
              DowUrk Financial Literacy Program
            </h2>
          </div>

          {/* Recipient */}
          <div className="mb-6">
            <p className="text-sm text-gray-600 mb-1">This certifies that</p>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-800 font-serif">
              {certificate.user_name}
            </h3>
          </div>

          {/* Achievement */}
          <div className="mb-6">
            <p className="text-sm text-gray-600 mb-2">has successfully completed</p>
            <div className="inline-block bg-[#006847]/10 px-4 py-2 rounded-lg">
              <h4 className="text-lg font-semibold text-[#006847]">
                {certificate.module_name || certificate.track_name}
              </h4>
              {certificate.track_name && certificate.module_name && (
                <p className="text-xs text-gray-600">{certificate.track_name} Track</p>
              )}
            </div>
          </div>

          {/* Certificate Details */}
          <div className="flex items-center justify-center gap-6 text-sm text-gray-600 mb-6">
            <div className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              <span>{new Date(certificate.issued_at).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}</span>
            </div>
            <div className="flex items-center gap-1">
              <Shield className="h-4 w-4 text-green-600" />
              <span className="font-mono text-xs">{certificate.certificate_number}</span>
            </div>
          </div>

          {/* PROOF Badge */}
          <div className="flex items-center justify-center gap-2 mb-6">
            <Badge className="bg-gradient-to-r from-[#006847] to-[#1E4D2E] text-white">
              <CheckCircle className="h-3 w-3 mr-1" />
              PROOF Verified
            </Badge>
          </div>

          {/* Signatures */}
          <div className="flex justify-around pt-4 border-t border-gray-200">
            <div className="text-center">
              <div className="h-12 flex items-end justify-center">
                <span className="font-serif italic text-gray-400 text-lg">RJ Brown</span>
              </div>
              <div className="border-t border-gray-300 pt-1 px-8">
                <p className="text-xs text-gray-500">Program Director</p>
              </div>
            </div>
            <div className="text-center">
              <div className="h-12 flex items-end justify-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_equity-focus/artifacts/875dlk3n_dowurk_color_shield.png" 
                  alt="DowUrk Seal" 
                  className="h-10 w-10 object-contain"
                />
              </div>
              <div className="border-t border-gray-300 pt-1 px-8">
                <p className="text-xs text-gray-500">Official Seal</p>
              </div>
            </div>
          </div>

          {/* Actions */}
          {showActions && (
            <div data-actions className="flex justify-center gap-2 mt-6 pt-4 border-t border-dashed border-gray-200">
              <Button size="sm" variant="outline" onClick={handleCopyLink}>
                <Copy className="h-4 w-4 mr-1" /> Copy Link
              </Button>
              <Button size="sm" variant="outline" onClick={handleDownloadPDF} disabled={downloading}>
                {downloading ? (
                  <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Generating...</>
                ) : (
                  <><Download className="h-4 w-4 mr-1" /> Download PDF</>
                )}
              </Button>
              <Button size="sm" variant="outline" onClick={handleShare}>
                <Share2 className="h-4 w-4 mr-1" /> Share
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}

// My Certificates page
export function MyCertificates() {
  const { user } = useAuth();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCertificates = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/financial-literacy/certificates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCertificates(data.certificates);
      }
    } catch (err) {
      console.error('Error fetching certificates:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchCertificates();
  }, [fetchCertificates]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#006847]" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <div className="container mx-auto max-w-2xl text-center">
          <Award className="h-16 w-16 mx-auto text-gray-300 mb-4" />
          <h1 className="text-2xl font-bold mb-4">My Certificates</h1>
          <p className="text-gray-600 mb-6">Please log in to view your certificates.</p>
          <Link to="/login">
            <Button>Log In</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4" data-testid="my-certificates">
      <div className="container mx-auto max-w-4xl">
        <Link to="/financial-literacy" className="text-[#006847] hover:underline flex items-center gap-1 mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to Financial Literacy Hub
        </Link>

        <div className="text-center mb-8">
          <Award className="h-12 w-12 mx-auto text-[#006847] mb-4" />
          <h1 className="text-3xl font-bold mb-2">My Certificates</h1>
          <p className="text-gray-600">
            Your earned certificates from the Financial Literacy Program
          </p>
        </div>

        {certificates.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <BookOpen className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium mb-2">No certificates yet</h3>
              <p className="text-gray-500 mb-4">
                Complete course modules to earn your first certificate!
              </p>
              <Link to="/financial-literacy/courses">
                <Button className="bg-[#006847] hover:bg-[#005438]">
                  Start Learning
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {certificates.map((cert) => (
              <CertificateCard key={cert.id} certificate={cert} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Verification page (public)
export function VerifyCertificate() {
  const { certificateNumber } = useParams();
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchNumber, setSearchNumber] = useState(certificateNumber || '');
  const [error, setError] = useState(null);

  const verifyCertificate = useCallback(async (certNum) => {
    if (!certNum) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const res = await fetch(`${API}/api/financial-literacy/certificates/verify/${certNum}`);
      const data = await res.json();
      
      if (data.valid) {
        setCertificate(data.certificate);
      } else {
        setError('Certificate not found. Please check the number and try again.');
        setCertificate(null);
      }
    } catch (err) {
      setError('Error verifying certificate. Please try again.');
      setCertificate(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (certificateNumber) {
      verifyCertificate(certificateNumber);
    } else {
      setLoading(false);
    }
  }, [certificateNumber, verifyCertificate]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchNumber) {
      verifyCertificate(searchNumber);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4" data-testid="verify-certificate">
      <div className="container mx-auto max-w-4xl">
        <div className="text-center mb-8">
          <Shield className="h-12 w-12 mx-auto text-[#006847] mb-4" />
          <h1 className="text-3xl font-bold mb-2">Verify Certificate</h1>
          <p className="text-gray-600">
            Enter a certificate number to verify its authenticity
          </p>
        </div>

        {/* Search Form */}
        <Card className="mb-8">
          <CardContent className="py-6">
            <form onSubmit={handleSearch} className="flex gap-2 max-w-md mx-auto">
              <Input
                type="text"
                placeholder="Enter certificate number (e.g., DU-FL-XXXXXXXX)"
                value={searchNumber}
                onChange={(e) => setSearchNumber(e.target.value.toUpperCase())}
                className="font-mono"
              />
              <Button type="submit" className="bg-[#006847] hover:bg-[#005438]">
                Verify
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Results */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#006847]" />
          </div>
        ) : error ? (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="py-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                <Shield className="h-8 w-8 text-red-500" />
              </div>
              <h3 className="text-lg font-medium text-red-800 mb-2">Verification Failed</h3>
              <p className="text-red-600">{error}</p>
            </CardContent>
          </Card>
        ) : certificate ? (
          <div>
            {/* Verification Success Banner */}
            <div className="bg-green-100 border border-green-300 rounded-lg p-4 mb-6 flex items-center gap-3">
              <CheckCircle className="h-6 w-6 text-green-600" />
              <div>
                <p className="font-medium text-green-800">Certificate Verified</p>
                <p className="text-sm text-green-600">
                  This is an authentic DowUrk Financial Literacy certificate.
                </p>
              </div>
            </div>
            
            <CertificateCard certificate={certificate} showActions={false} />
          </div>
        ) : !certificateNumber ? (
          <Card className="text-center py-12">
            <CardContent>
              <Shield className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium mb-2">Enter a Certificate Number</h3>
              <p className="text-gray-500">
                Use the search box above to verify a certificate
              </p>
            </CardContent>
          </Card>
        ) : null}
      </div>
    </div>
  );
}

export default CertificateCard;
